import * as React from "react";
import { Toggle as BaseToggle } from "react-ui";
import { useEffect } from "react";

import { ControlValidationProps } from "../../Validations/ValidationProps";
import { ControlTooltipProps } from "../../Internals/Tooltip/ControlTooltipProps";
import { AbsoluteModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { useScrollTo } from "../../Internals/Contexts/ScrollToContext";
import { emptyRef } from "../../TypingUtils";
import { noop } from "../../../../../Common/TypingUtils";
import { FocusManagementObserver } from "../../Services/FocusManagement/FocusManagementObserver";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { TOGGLE } from "../../metricsAction";
import { useTestPageObject } from "../../Services/TestPageObjects/PageObjectExposure";

import { ToggleTestPageObject } from "./ToggleTestPageObject";

export type ToggleValue = string | boolean;
export type ToggleCaptionPosition = "right" | "left" | undefined;

export interface ToggleProps extends ControlValidationProps, ControlTooltipProps {
    bindingPath: AbsoluteModelPath;
    checkedValue?: ToggleValue;
    uncheckedValue?: ToggleValue;
    value: ToggleValue;
    children?: React.ReactNode;
    disabled?: boolean;
    setError?: (isError: boolean) => void;
    onChange: (value: undefined | ToggleValue) => void;
    setCorrectValue: (value: undefined | ToggleValue) => void;
    isReadOnly?: boolean;
    "data-tid"?: string;
    captionPosition?: ToggleCaptionPosition;
}

export function Toggle(props: ToggleProps): JSX.Element {
    const track = useMetrics();
    const scrollTo = useScrollTo();
    const container = React.useRef<HTMLDivElement>(emptyRef);
    const baseToggle = React.useRef<BaseToggle>(emptyRef);

    const {
        value,
        onChange,
        setCorrectValue,
        error,
        warning,
        checkedValue = true,
        uncheckedValue = false,
        setError = noop,
    } = props;

    const handleChange = (checked?: boolean) => {
        let nextValue = checkedValue;
        if (checked !== undefined) {
            nextValue = checked ? checkedValue : uncheckedValue;
        } else if (value === checkedValue || value !== uncheckedValue) {
            nextValue = uncheckedValue;
        }
        if (error) {
            setError(false);
        }
        onChange(nextValue);
        track(TOGGLE, {
            path: props.bindingPath.toString(),
            isChecked: nextValue === checkedValue ? true : false,
        });
    };

    useEffect(() => {
        if (value !== checkedValue) {
            setCorrectValue(uncheckedValue);
        }
    }, [value]);

    const handleRequestFocus = () => {
        if (container.current != undefined) {
            scrollTo(container.current);
        }
        baseToggle.current?.focus();
    };

    const tooltipText = error ?? warning ?? "";

    useTestPageObject(
        () => ToggleTestPageObject(value, checkedValue, handleChange),
        [value, checkedValue, handleChange]
    );

    return (
        <FocusManagementObserver onRequestFocus={handleRequestFocus}>
            {props.isReadOnly ? (
                <BaseToggle
                    ref={baseToggle}
                    checked={props.value === checkedValue}
                    disabled={true}
                    data-tid={props["data-tid"]}>
                    {props.children}
                </BaseToggle>
            ) : (
                <Tooltip
                    data-tid={"Tooltip"}
                    useWrapper={false}
                    pos={props.tooltipPosition ?? "top left"}
                    trigger={tooltipText ? "hover&focus" : "closed"}
                    value={tooltipText}
                    width={props.tooltipWidth}>
                    <span
                        onClick={e => {
                            e.stopPropagation();
                        }}>
                        <BaseToggle
                            ref={baseToggle}
                            checked={props.value === checkedValue}
                            disabled={props.disabled}
                            error={Boolean(error)}
                            warning={Boolean(warning)}
                            data-tid={props["data-tid"]}
                            onValueChange={handleChange}
                            captionPosition={props.captionPosition}>
                            {props.children}
                        </BaseToggle>
                    </span>
                </Tooltip>
            )}
        </FocusManagementObserver>
    );
}
